import { Countdown } from '../../types';
import { NavRoute } from './types';
import { DASHBOARD_HERO_PARTICLES } from '../../dashboardHomeConstants';

const pad2 = (n: number) => String(Math.max(0, n)).padStart(2, '0');

const COUNTDOWN_PARTS: Array<{ key: keyof Countdown; label: string; pad: boolean }> = [
  { key: 'days', label: 'days', pad: false },
  { key: 'hours', label: 'hrs', pad: true },
  { key: 'minutes', label: 'min', pad: true },
  { key: 'seconds', label: 'sec', pad: true },
];

const SparkleIcon = () => (
  <svg width='16' height='16' viewBox='0 0 24 24' fill='none' aria-hidden='true'>
    <path
      d='M12 2l1.8 5.4L19 9.2l-5.2 1.8L12 16.4l-1.8-5.4L5 9.2l5.2-1.8L12 2z'
      fill='#f5a623'
    />
    <path
      d='M19 14l.9 2.7 2.7.9-2.7.9-.9 2.7-.9-2.7-2.7-.9 2.7-.9.9-2.7z'
      fill='#f5a623'
      opacity='0.85'
    />
  </svg>
);

const HERO_CHIPS = ['Build credit', 'Earn rewards', 'No fees to start'] as const;

const formatCountdownValue = (value: number, pad: boolean) =>
  pad ? pad2(value) : String(Math.max(0, value));

const currentMonthLabel = () =>
  new Date().toLocaleString('default', { month: 'long' }).toUpperCase();

export const WelcomeHero = ({
  countdown,
  countdownPending,
  onNav,
}: {
  countdown: Countdown;
  countdownPending: boolean;
  onNav: (route: NavRoute) => void;
}) => (
  <div className='hero welcome-hero'>
    {DASHBOARD_HERO_PARTICLES.map((particle) => (
      <div
        key={`${particle.top}-${particle.left}`}
        className={`sv-particle ${particle.tone}`}
        style={{
          top: particle.top,
          left: particle.left,
          width: particle.width,
          height: particle.height,
          animationDelay: particle.delay,
          animationDuration: particle.duration,
        }}
        aria-hidden='true'
      />
    ))}
    <div className='hero-glow-wm hero-glow-blue' aria-hidden='true' />
    <div className='hero-shimmer-edge' aria-hidden='true' />

    <div className='hero-body mt-[28px] mb-[28px]'>
      <div className='hero-l'>
        <div className='hero-meta'>
          <div className='badge badge-welcome'>Welcome to TenantPay</div>
        </div>
        <h1 className='hero-h1 welcome-hero-h1'>
          Make your first
          <br />
          <strong>rent payment.</strong>
        </h1>
        <p className='hero-p mb-[20px]'>
          Add your rental and pay in a few taps. Build credit with every payment and unlock rewards
          from day one.
        </p>
        <div className='welcome-hero-chips'>
          {HERO_CHIPS.map((chip) => (
            <span key={chip} className='welcome-hero-chip'>
              {chip}
            </span>
          ))}
        </div>
        <div className='welcome-hero-cta-row'>
          <button type='button' className='welcome-hero-cta' onClick={() => onNav('pay-rent')}>
            Pay rent now
            <svg
              width='14'
              height='14'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2.5'
              strokeLinecap='round'
              strokeLinejoin='round'
              aria-hidden='true'
            >
              <polyline points='9 18 15 12 9 6' />
            </svg>
          </button>
          <span className='welcome-hero-cta-hint'>Setup takes about 2 minutes</span>
        </div>
      </div>
      <div className='welcome-drop-card'>
        <div className='welcome-drop-glow' aria-hidden='true' />
        <div className='welcome-drop-head'>
          <div className='welcome-drop-icon'>
            <SparkleIcon />
          </div>
          <div className='welcome-drop-head-copy'>
            <div className='welcome-drop-lbl'>{currentMonthLabel()} rent drop</div>
            <div className='welcome-drop-sub'>Pay on time to enter</div>
          </div>
        </div>
        <h2 className='welcome-drop-title'>Your rent could be on us.</h2>
        <p className='welcome-drop-body'>
          Make your first on-time payment to enter, win up to 1 month free.
        </p>
        <div className='welcome-drop-countdown'>
          <div className='welcome-drop-cd-lbl'>Drop closes in</div>
          <div className='welcome-drop-cd-row'>
            {COUNTDOWN_PARTS.map((part) => (
              <div key={part.key} className='welcome-drop-cd-box'>
                <span className='welcome-drop-cd-num'>
                  {countdownPending ? '-' : formatCountdownValue(countdown[part.key], part.pad)}
                </span>
                <span className='welcome-drop-cd-sub'>{part.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);
